import { customAlphabet } from 'nanoid'
import semver from 'semver'
import { z } from 'zod'

/** Parse an value from a given schema with optional error or null return */
export function schemaModel<O, I>(
  data: I,
  schema: z.ZodType<O, I>,
  throwError?: true,
): O
export function schemaModel<O, I>(
  data: I,
  schema: z.ZodType<O, I>,
  throwError?: false,
): O | null
export function schemaModel<O, I>(
  data: I,
  schema: z.ZodType<O, I>,
  throwError = true,
): O | null {
  const result = schema.safeParse(data)

  if (!result.success) {
    console.error('Zod Schema Error')
    console.group()
    result.error.issues.forEach((issue) => {
      console.log(`Path: ${issue.path.join(', ')} \nError: ${issue.message}`)
    })

    console.groupEnd()
  }

  if (throwError && !result.success) throw new Error('Zod validation failure')

  return result.success ? result.data : null
}

export const NANOID_ALPHABET =
  'useandom26T198340PX75pxJACKVERYMINDBUSHWOLFGQZbfghjklqvwyzrict'

/**
 * Generate secure URL-friendly unique ID.
 *
 * By default, the ID will have 21 symbols to have a collision probability
 * similar to UUID v4.
 *
 * ```js
 * import { nanoid } from '@scalar-org/entities/utility'
 * model.id = nanoid() //=> "UakgbKJ5m9gl0JDMbcJqL"
 * ```
 *
 * @param size Size of the ID. The default size is 21.
 * @returns A random string.
 */
export const nanoid = (size?: number): string => {
  return customAlphabet(NANOID_ALPHABET, 21)(size)
}

export const emailSchema = z
  .email()
  .overwrite((s) => s.toLowerCase())
  .meta({ id: 'email' })

export const timestampSchema = z.number().int().min(0).meta({ id: 'timestamp' })
export const nanoidSchema = z.string().min(5).meta({ id: 'nanoid' })

/**
 * Shared based entity schema for defining
 * top level entities
 */
export const entitySchema = z
  .object({
    uid: nanoidSchema.default(nanoid).meta({ default: 'nanoid()' }),
    createdAt: timestampSchema
      .default(unixTimestamp)
      .meta({ default: 'unixTimestamp()' }),
    updatedAt: timestampSchema
      .default(unixTimestamp)
      .meta({ default: 'unixTimestamp()' }),
  })
  .meta({ id: 'entity', default: 'nanoid()' })

export type Entity = z.infer<typeof entitySchema>

/** Unix timestamp in seconds. Default to current time */
export function unixTimestamp(
  /** Javascript date or timestamp in milliseconds */
  date: Date | number | string = Date.now(),
) {
  return Math.floor(new Date(date).getTime() / 1000)
}

export enum Environments {
  Dev = 'development',
  Test = 'test',
  Prod = 'production',
  Staging = 'staging',
}

export type DeployEnvironments =
  | 'development'
  | 'test'
  | 'staging'
  | 'production'

export const deployEnvironmentsSchema = z
  .enum(Environments)
  .optional()
  .default(Environments.Dev)
  .meta({ id: 'deploy-environments' })

/**
 * This RegEx is valid as of 2024-11-06.
 * If it reports false positives or false negatives, please check the website for an update!
 * See here: https://semver.org/#is-there-a-suggested-regular-expression-regex-to-check-a-semver-string
 *
 * Uses numbered capture groups for better javascript support
 */
export const regexSemverNumberedGroups =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/

export const semverSchema = z
  .string()
  .regex(regexSemverNumberedGroups, {
    message: 'Version must be a valid semver format (e.g. 0.1.0)',
  })
  .meta({ id: 'semver' })

/** Reserved version identifiers that cannot be used as user-created version names */
export const RESERVED_VERSION_NAMES = ['latest'] as const

/** Flexible version string -- accepts any non-empty string except reserved aliases */
export const docVersionSchema = z
  .string()
  .min(1, { message: 'Version must be a non-empty string' })
  .refine((v) => !RESERVED_VERSION_NAMES.includes(v.toLowerCase() as any), {
    message: `Version cannot be a reserved name: ${['latest'].join(', ')}`,
  })
  .meta({ id: 'version' })

/**
 * Compare two version strings for descending sort.
 * Semver-valid versions always rank above non-semver strings.
 * Among semver versions, semantic ordering is used.
 * Among non-semver strings, alphanumeric descending is used.
 */
export function compareVersionsDesc(a: string, b: string): number {
  const aValid = semver.valid(a)
  const bValid = semver.valid(b)

  if (aValid && bValid) return semver.compare(b, a)
  if (aValid && !bValid) return -1
  if (!aValid && bValid) return 1
  return b.localeCompare(a)
}

/** Check if `version` is greater than `other`, consistent with compareVersionsDesc */
export function isVersionGreater(version: string, other: string): boolean {
  const vValid = semver.valid(version)
  const oValid = semver.valid(other)

  if (vValid && oValid) return semver.gt(version, other)
  if (vValid && !oValid) return true
  if (!vValid && oValid) return false
  return version.localeCompare(other) > 0
}

/** Check if `version` is greater than or equal to `other`, supporting mixed semver/string */
export function isVersionGte(version: string, other: string): boolean {
  if (version === other) return true
  return isVersionGreater(version, other)
}

/**
 * Suggest a next version string given the current latest version.
 *
 * Recognises several common patterns:
 *  - Semver (1.2.3) → patch increment via semver.inc
 *  - Date with suffix (2025-01-15.hotfix) → today's date with same suffix
 *  - Date YYYY-MM-DD (2025-01-15) → today's date
 *  - Date YYYY-MM (2025-01) → current year-month
 *  - Sequential V/v prefix (V3, v12) → increment the number
 *
 * Falls back to returning the input unchanged when no pattern matches.
 */
export function suggestNextVersion(current: string): string {
  const inc = semver.inc(current, 'patch')
  if (inc) return inc

  const today = new Date()
  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0')
  const dd = String(today.getDate()).padStart(2, '0')

  const dateSuffix = current.match(/^(\d{4}-\d{2}-\d{2})\.(.+)$/)
  if (dateSuffix) return `${yyyy}-${mm}-${dd}.release-name`

  if (/^\d{4}-\d{2}-\d{2}$/.test(current)) return `${yyyy}-${mm}-${dd}`

  if (/^\d{4}-\d{2}$/.test(current)) return `${yyyy}-${mm}`

  const vNum = current.match(/^([Vv])(\d+)$/)
  if (vNum) return `${vNum[1]}${Number(vNum[2]) + 1}`

  return current
}

/** Convert an array of objects to an object with their uid as the key */
export function arrayToMap<T extends { uid: string }>(array: T[]) {
  return array.reduce<Record<string, T>>((acc, item) => {
    acc[item.uid] = item
    return acc
  }, {})
}

/** Unwrap a complex type to reveal the underlying primitive types */
export type Prettify<T> = {
  [K in keyof T]: T[K]
} & {}

/**
 * RegEx for slug validation
 *
 * Valid Examples:
 * monkey-climbs-trees, kangaroo--jumps---around
 *
 * Invalid Examples:
 * 1test-example.com, -test, example-
 */
export const regexSlug = /^[a-z](?:[a-z0-9-]*[a-z0-9])?$/

/**
 * Zod Schema for slug validation, with a reasonable max length.
 */
export const slugSchema = z
  .string()
  .min(1, 'Slug is required')
  .min(3, 'Slug must be at least 3 characters')
  .max(60, 'Slug must be less than 60 characters')
  .regex(regexSlug, {
    message:
      'Slug can have lowercase letters, digits, or hyphens. It must start with a lowercase letter and end with a letter or number.',
  })
  .meta({ id: 'slug' })

const setPreprocess = (input: unknown) =>
  input instanceof Set ? input : Array.isArray(input) ? new Set(input) : input

const setTransform = <T>(set: Set<T>) => Array.from(set)

export const scalarSet = <T extends z.ZodType>(zodSetType: z.ZodSet<T>) =>
  z.preprocess(setPreprocess, zodSetType).transform(setTransform<z.output<T>>)

/** See @scalar-org/helpers/url/slugify.ts */
export const slugify = (v: string) =>
  v
    .normalize('NFC')
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{M}\p{N}\s-]/gu, '')
    .replace(/[\s-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 255)

export const originSchema = z
  .codec(z.string(), z.array(z.union([z.string(), z.instanceof(RegExp)])), {
    decode: (arg) =>
      arg
        .split(',')
        .map((s) => s.trim())
        .filter((s) => !!s)
        .map((segment) => {
          if (segment.at(0) !== '^') return segment
          try {
            return new RegExp(segment)
          } catch {
            return segment
          }
        }),
    encode: (arr) =>
      arr.map((x) => (typeof x === 'string' ? x : x.source)).join(', '),
  })
  .meta({ id: 'origin' })

/**
 * Registry Namespace for a team.
 */
export const namespaceSchema = z
  .string()
  .regex(
    /^[a-zA-Z0-9-_]+$/,
    'Namespaces can only contain letters, numbers, "-", and "_".',
  )
  .toLowerCase()
  .min(3, 'Namespaces need to be at least 3 characters.')
  .max(50, 'Namespaces need to be less than 50 characters.')
  .meta({ id: 'namespace' })

/** Zod Record<string | number | symbol, any> */
export const objectSchema = z
  .record(z.union([z.string(), z.number(), z.symbol()]), z.any())
  .meta({ id: 'object' })

/**
 * Extract inner schema from Zod type
 *
 * For Zod pipes (transform and effects), it prioritises
 * whichever .in or .out is an object. If both .in and .out
 * are objects, we prioritise the out type
 *
 * */
export function zodInnerType(schema: z.ZodType) {
  if (!(schema instanceof z.ZodPipe)) return schema

  if (schema.out instanceof z.ZodObject) return schema.out

  if (schema.in instanceof z.ZodObject) return schema.in

  return schema.in
}

export class ErrorWithCtx extends Error {
  context = {}

  constructor(message: string, { context }: { context?: object | null }) {
    super(message)
    this.name = 'ErrorWithCtx'
    if (context) this.context = context
  }
}

/** All registry entities are identified by this metadata */
export const registryMetaSchema = z
  .object({
    namespace: z.string(),
    slug: z.string(),
  })
  .meta({ id: 'registry-meta' })

export type RegistryMeta = z.infer<typeof registryMetaSchema>
